import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
// Public shop/content catalog RPCs — session-free to avoid GoTrue AbortError.
import { supabasePublic as supabase } from "../../../config/supabaseClient";
import { getShoppableClicks } from "../../../Utils/SupabaseServices/ContentDatabase";
import { getTemplateShoppableClicks } from "../../../Utils/SupabaseServices/TemplateAnalyticsDatabase";
import {
  parseProductBreadcrumbs,
  productMatchesBagsPursesPrimaryAlias,
  type ParsedBreadcrumbs,
} from "./shopBreadcrumbs";
import {
  normalizeShopUrl as firstShopUrl,
  resolvePostShopUrl,
  resolveShopButtonCta,
} from "../../../Utils/normalizeShopUrl";
import {
  isLikelyVideoUrl,
  resolvePlayableVideoUrl,
} from "../../../Utils/media/feedMedia";
import { sanitizePublicCardTitle } from "../../../Utils/media/feedCardTitle";
import { splitTemplateMediaUrls } from "./templatePostMedia";

dayjs.extend(utc);
dayjs.extend(timezone);

export type TemplateShopView =
  | "all"
  | "trending"
  | "category"
  | "content"
  | "brand";

export type ShopCatalogProduct = {
  postId: string;
  name: string;
  url: string;
  image: string;
  contentCategory: string;
  platform: string;
  breadcrumbsRaw: string;
  breadcrumbs: ParsedBreadcrumbs | null;
  /** Social permalink — used when shop_link is empty so content posts stay clickable. */
  permalink?: string;
};

/** Content (non-PRODUCT) shown on the Content page. `shoppable` only for true `shop_link`. */
export type ShopCatalogContent = {
  postId: string;
  caption: string;
  shopUrl: string;
  shoppable: boolean;
  /** Shop / View / Watch — from resolveShopButtonCta priority */
  ctaLabel: "Shop" | "View" | "Watch";
  permalink: string;
  image: string;
  mediaUrl: string;
  /** Multi-slide `media_url` (website Content catalog carousel only). */
  mediaUrls?: string[];
  thumbnailUrls?: string[];
  isVideo: boolean;
  isText: boolean;
  isCarousel: boolean;
  platform: string;
  platformSlug: string;
  type: string;
  formatSlug: string | null;
  dateLabel: string;
};

export type ShopContentNavItem = {
  label: string;
  slug: string;
  count: number;
};

const PRODUCT_PLATFORMS = new Set([
  "ltk",
  "shopmy",
  "amazon",
  "misc",
  "misc-product",
]);

function isProductPlatform(platform: string): boolean {
  return PRODUCT_PLATFORMS.has(platform.trim().toLowerCase());
}

function isMiscContentPlatform(platform: string): boolean {
  const p = platform.trim().toLowerCase();
  return p === "misc-content" || p === "misc_content";
}

const CONTENT_COLS_MID =
  "post_id, content_title, caption, shop_link, tagged_link, external_link, permalink, media_url, thumbnail, platform, type, post_date";
const CONTENT_COLS_MIN =
  "post_id, shop_link, media_url, thumbnail, platform, type, post_date";
// Legacy PostgREST max page size (fallback cap when RPCs are not deployed).
// Prefer TEMPLATE_SHOP_PAGE_SIZE via fetchTemplateShopContentPage({ limit }).
export const CONTENT_PAGE_SIZE = 1000;
export const CONTENT_MAX_ROWS = 50000;
/** Demand-driven grid page size — matches UI chunk / IntersectionObserver. */
export const TEMPLATE_SHOP_PAGE_SIZE = 25;

function isVideoContentType(type: string, platform: string, media: string): boolean {
  const t = type.toUpperCase();
  if (
    t === "TIKTOK" ||
    t === "REELS" ||
    t === "VIDEO" ||
    t === "PODCAST"
  ) {
    return true;
  }
  const p = platform.toLowerCase();
  if (p === "tiktok" || p === "youtube" || p === "podcast") return true;
  return Boolean(media) && isLikelyVideoUrl(media);
}

function isTextContentType(type: string, platform: string): boolean {
  const t = type.toUpperCase();
  const p = platform.toLowerCase();
  if (t === "TWEET" || t === "LINKEDIN" || t === "TWITTER") return true;
  return p === "twitter" || p === "linkedin" || p === "x";
}

function firstMediaPart(raw: string): string {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)[0] ?? "";
}

function stillUrl(raw: unknown): string {
  const first = firstMediaPart(String(raw ?? "").trim());
  if (first && !isLikelyVideoUrl(first)) return first;
  return "";
}

function contentVideoUrl(row: Record<string, unknown>): string {
  return resolvePlayableVideoUrl({
    mediaUrl: String(row.media_url ?? "").trim(),
    storageUrl: String(row.storage_url ?? "").trim(),
    type: String(row.type ?? "").trim(),
    platform: String(row.platform ?? "").trim(),
  });
}

function contentImageUrl(row: Record<string, unknown>): string {
  const thumbs = [
    String(row.thumbnail ?? "").trim(),
    String(row.thumbnail_url ?? "").trim(),
  ].filter(Boolean);
  for (const t of thumbs) {
    const first = firstMediaPart(t);
    if (first && !isLikelyVideoUrl(first)) return first;
  }
  const media = String(row.media_url ?? "").trim();
  const first = firstMediaPart(media);
  if (first && !isLikelyVideoUrl(first)) return first;
  return "";
}

export function slugifyShopContentPlatform(platform: string): string {
  const p = platform.trim().toLowerCase();
  if (p === "x" || p === "twitter") return "twitter";
  if (isMiscContentPlatform(p)) return "others";
  return p.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function shopContentPlatformLabel(slug: string): string {
  const s = slug.trim().toLowerCase();
  if (s === "instagram") return "Instagram";
  if (s === "tiktok") return "TikTok";
  if (s === "youtube") return "YouTube";
  if (s === "twitter" || s === "x") return "X";
  if (s === "linkedin") return "LinkedIn";
  if (s === "pinterest") return "Pinterest";
  if (s === "podcast") return "Podcast";
  if (s === "newsletter") return "Newsletter";
  if (s === "others" || s === "other" || isMiscContentPlatform(s)) return "Other";
  return slug
    .trim()
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Second-level Content chip. Only Instagram splits by `type`;
 * other platforms are platform-only (no type row).
 *   FEED            → Posts
 *   REELS           → Reels
 *   STORY/HIGHLIGHTS → Stories
 */
export function shopContentFormatSlug(type: string, platformSlug: string): string | null {
  const t = type.trim().toUpperCase();
  if (platformSlug === "instagram") {
    if (t === "REELS") return "reels";
    if (t === "STORY" || t === "HIGHLIGHTS") return "stories";
    if (t === "FEED" || t === "POST") return "posts";
    return "posts";
  }
  if (platformSlug === "youtube") {
    if (t === "SHORTS" || t === "SHORT" || t === "REELS") return "shorts";
    return "videos";
  }
  return null;
}

export function shopContentFormatLabel(formatSlug: string): string {
  const s = formatSlug.trim().toLowerCase();
  if (s === "reels") return "Reels";
  if (s === "stories") return "Stories";
  if (s === "posts") return "Posts";
  if (s === "shorts") return "Shorts";
  if (s === "videos") return "Videos";
  return formatSlug;
}

export function shopContentCardLabel(item: ShopCatalogContent): string {
  const p = item.platformSlug;
  const t = item.type.trim().toUpperCase();
  if (item.isText) {
    return p === "linkedin" ? "LinkedIn post" : "X post";
  }
  if (p === "instagram") {
    if (item.formatSlug === "reels" || t === "REELS") return "Instagram Reel";
    if (item.formatSlug === "stories" || t === "STORY" || t === "HIGHLIGHTS") {
      return "Instagram Story";
    }
    return "Instagram Post";
  }
  if (p === "tiktok") return "TikTok";
  if (p === "youtube") {
    return item.formatSlug === "shorts" || t === "SHORTS" || t === "SHORT"
      ? "YouTube Short"
      : "YouTube video";
  }
  if (p === "pinterest") return "Pinterest";
  if (p === "podcast") return "Podcast";
  if (p === "newsletter") return "Newsletter";
  if (p === "others" || p === "other") return "Other";
  return shopContentPlatformLabel(p);
}

function mapUsernameContentRow(
  row: Record<string, unknown>,
): ShopCatalogContent | null {
  const postId = row.post_id != null ? String(row.post_id).trim() : "";
  if (!postId) return null;
  const cta = resolveShopButtonCta(row);
  const shopUrl = cta?.url || "";
  const ctaLabel = cta?.label || "Shop";
  const shoppable = cta?.kind === "shop";
  const type = String(row.type ?? "").trim();
  if (type.toUpperCase() === "PRODUCT") return null;
  const platform = String(row.platform ?? "").trim();
  if (isProductPlatform(platform)) return null;
  const platformSlug = slugifyShopContentPlatform(platform);
  if (!platformSlug) return null;

  const mediaRaw = String(row.media_url ?? "").trim();
  const mediaParts = mediaRaw.split(",").map((s) => s.trim()).filter(Boolean);
  const mediaUrls = splitTemplateMediaUrls(mediaRaw);
  const image = contentImageUrl(row);
  const videoUrl = contentVideoUrl(row);
  const isVideo = Boolean(videoUrl) && isVideoContentType(type, platform, mediaRaw);
  const isText =
    isTextContentType(type, platform) && !image && !videoUrl;
  const caption = sanitizePublicCardTitle(
    row.caption ?? row.content_title ?? "",
    row,
  );
  // LinkedIn / X cards are caption-led (text tiles or media + caption).
  // Empty-caption rows render as blank cards — drop them here.
  if (
    (platformSlug === "linkedin" ||
      platformSlug === "twitter" ||
      platformSlug === "x") &&
    !caption
  ) {
    return null;
  }
  const permalink =
    firstShopUrl(row.permalink) ||
    firstShopUrl(row.url) ||
    firstShopUrl(row.table_url) ||
    (!shoppable ? shopUrl : "") ||
    "";
  const postDate = String(row.post_date ?? "").trim();
  const dateLabel = postDate ? dayjs(postDate).format("MMM D") : "";

  if (!image && !isText && !caption && !shopUrl && !permalink) return null;

  return {
    postId,
    caption,
    shopUrl,
    shoppable,
    ctaLabel,
    permalink,
    image,
    mediaUrl: videoUrl,
    ...(mediaUrls.length > 1
      ? {
          mediaUrls,
          thumbnailUrls: splitTemplateMediaUrls(String(row.thumbnail ?? "")),
        }
      : {}),
    isVideo: Boolean(videoUrl) && isVideo,
    isText,
    isCarousel: mediaParts.length > 1,
    platform,
    platformSlug,
    type,
    formatSlug: shopContentFormatSlug(type, platformSlug),
    dateLabel,
  };
}

/** Map a live search / feed post onto the Content-page catalog card. */
export function mapFeedPostToShopCatalogContent(
  post: Record<string, unknown>,
  shopLinkOverride?: string,
): ShopCatalogContent | null {
  const shop =
    firstShopUrl(post.shop_link) || firstShopUrl(shopLinkOverride);
  return mapUsernameContentRow({
    ...post,
    shop_link: shop || post.shop_link,
  });
}

/** Map a live search / feed post onto the Products-page catalog card. */
export function mapFeedPostToShopCatalogProduct(
  post: Record<string, unknown>,
  shopLinkOverride?: string,
  creatorUsername?: string | null,
): ShopCatalogProduct | null {
  const platform = String(post.platform ?? "");
  const type = String(post.type ?? "").toUpperCase();
  if (type !== "PRODUCT" && !isProductPlatform(platform)) return null;
  return mapUsernameProductRow(
    {
      ...post,
      shop_link:
        firstShopUrl(post.shop_link) ||
        firstShopUrl(shopLinkOverride) ||
        firstShopUrl(post.external_link) ||
        post.shop_link,
      product_name:
        post.product_name || post.content_title || post.shop_title || "",
    },
    creatorUsername ||
      String(post.username ?? post.user_name ?? post.handle ?? "").trim() ||
      null,
  );
}

export function buildShopContentPlatformIndex(
  items: ShopCatalogContent[],
): { platforms: ShopContentNavItem[]; formatsByPlatform: Record<string, ShopContentNavItem[]> } {
  const platformCounts = new Map<string, { label: string; count: number }>();
  const formatCounts = new Map<string, Map<string, { label: string; count: number }>>();

  for (const item of items) {
    const prev = platformCounts.get(item.platformSlug);
    if (prev) prev.count += 1;
    else {
      platformCounts.set(item.platformSlug, {
        label: shopContentPlatformLabel(item.platformSlug),
        count: 1,
      });
    }
    if (item.formatSlug) {
      if (!formatCounts.has(item.platformSlug)) {
        formatCounts.set(item.platformSlug, new Map());
      }
      const map = formatCounts.get(item.platformSlug)!;
      const f = map.get(item.formatSlug);
      if (f) f.count += 1;
      else {
        map.set(item.formatSlug, {
          label: shopContentFormatLabel(item.formatSlug),
          count: 1,
        });
      }
    }
  }

  const platforms: ShopContentNavItem[] = [...platformCounts.entries()]
    .sort((a, b) => b[1].count - a[1].count || a[1].label.localeCompare(b[1].label))
    .map(([slug, v]) => ({ slug, label: v.label, count: v.count }));

  const formatsByPlatform: Record<string, ShopContentNavItem[]> = {};
  for (const [platform, map] of formatCounts.entries()) {
    formatsByPlatform[platform] = [...map.entries()]
      .sort((a, b) => b[1].count - a[1].count || a[1].label.localeCompare(b[1].label))
      .map(([slug, v]) => ({ slug, label: v.label, count: v.count }));
  }

  return { platforms, formatsByPlatform };
}

export function contentMatchesShopFilter(
  item: ShopCatalogContent,
  platformSlug?: string | null,
  formatSlug?: string | null,
  shoppable?: "all" | "yes" | "no" | null,
): boolean {
  const p = String(platformSlug ?? "").trim().toLowerCase();
  const f = String(formatSlug ?? "").trim().toLowerCase();
  if (p && item.platformSlug !== p) return false;
  if (f && item.formatSlug !== f) return false;
  if (shoppable === "yes" && !item.shoppable) return false;
  if (shoppable === "no" && item.shoppable) return false;
  return true;
}

function stripTaggedLinkCols(cols: string): string {
  return cols.replace(/,\s*tagged_link/g, "").replace(/tagged_link,\s*/g, "");
}

function stripExternalLinkCols(cols: string): string {
  return cols.replace(/,\s*external_link/g, "").replace(/external_link,\s*/g, "");
}

type ContentQueryShape = { selectCols: string };

const contentShapeByTable = new Map<string, ContentQueryShape>();

function contentShapeKey(username: string, userId: string): string {
  return `${username}\0${userId}`;
}

const CONTENT_COL_CANDIDATES = [
  CONTENT_COLS_MID,
  stripExternalLinkCols(CONTENT_COLS_MID),
  stripTaggedLinkCols(CONTENT_COLS_MID),
  stripTaggedLinkCols(stripExternalLinkCols(CONTENT_COLS_MID)),
  CONTENT_COLS_MIN,
];

async function queryShopContentPage(args: {
  username: string;
  userId: string;
  selectCols: string;
  rangeFrom: number;
  rangeTo: number;
}): Promise<{ rows: Record<string, unknown>[]; error: { message?: string } | null }> {
  const { data, error } = await supabase
    .from(args.username)
    .select(args.selectCols)
    .eq("user_id", args.userId)
    .eq("index", "active")
    .neq("type", "PRODUCT")
    .order("post_date", { ascending: false })
    .range(args.rangeFrom, args.rangeTo);
  return { rows: (data as Record<string, unknown>[] | null) ?? [], error };
}

export type ShopContentPage = {
  items: ShopCatalogContent[];
  rawCount: number;
  pageSize: number;
};

function mapContentRows(rows: Record<string, unknown>[]): ShopCatalogContent[] {
  return rows
    .map((row) => mapUsernameContentRow(row))
    .filter((c): c is ShopCatalogContent => Boolean(c));
}

/** One PostgREST page of shoppable content. Used by infinite query so the grid can paint after page 1. */
export async function fetchTemplateShopContentPage(args: {
  username: string;
  userId: string;
  offset?: number;
  /** Rows to fetch. Defaults to TEMPLATE_SHOP_PAGE_SIZE (not the legacy 1000). */
  limit?: number;
}): Promise<ShopContentPage> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const offset = Math.max(0, args.offset ?? 0);
  const pageSize = Math.max(
    1,
    Math.min(args.limit ?? TEMPLATE_SHOP_PAGE_SIZE, CONTENT_PAGE_SIZE),
  );
  const empty: ShopContentPage = { items: [], rawCount: 0, pageSize };
  if (!username || !userId) return empty;

  const key = contentShapeKey(username, userId);
  const cached = contentShapeByTable.get(key);
  const candidates = cached ? [cached.selectCols] : CONTENT_COL_CANDIDATES;

  for (const selectCols of candidates) {
    const page = await queryShopContentPage({
      username,
      userId,
      selectCols,
      rangeFrom: offset,
      rangeTo: offset + pageSize - 1,
    });
    if (page.error) {
      console.warn(
        "[templateShopCatalog] shop content page failed, retrying:",
        page.error.message,
      );
      continue;
    }
    if (!cached) contentShapeByTable.set(key, { selectCols });
    return {
      items: mapContentRows(page.rows),
      rawCount: page.rows.length,
      pageSize,
    };
  }

  console.warn("[templateShopCatalog] fetch shoppable content page failed");
  return empty;
}

export async function fetchTemplateShopContent(args: {
  username: string;
  userId: string;
  limit?: number;
}): Promise<ShopCatalogContent[]> {
  const first = await fetchTemplateShopContentPage({
    username: args.username,
    userId: args.userId,
    offset: 0,
  });
  return first.items.slice(0, args.limit ?? first.items.length);
}

const BASE_PRODUCT_COLS =
  "post_id, product_name, shop_link, media_url, thumbnail, content_category, platform, post_date";

const PRODUCT_COLS_WITH_BREADCRUMBS = `${BASE_PRODUCT_COLS}, breadcrumbs`;

// Raised so creators with large shops see all products — infinite scroll
// handles DOM performance client-side.
const DEFAULT_FETCH_LIMIT = 2000;
/** Legacy PostgREST browse-index page size (capped; was 1000). */
export const BROWSE_PAGE_SIZE = 100;
export const BROWSE_INDEX_MAX_ROWS = 50000;
const TRENDING_DAYS = 30;
const MAX_CATEGORY_TABS = 5;

export function slugifyShopCategory(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function isMissingOptionalColumn(
  error: { message?: string } | null,
  column: string,
): boolean {
  const msg = String(error?.message ?? "").toLowerCase();
  const col = column.toLowerCase();
  return msg.includes(col) && (msg.includes("column") || msg.includes("does not exist"));
}

function isMissingBreadcrumbsColumn(error: { message?: string } | null): boolean {
  return isMissingOptionalColumn(error, "breadcrumbs");
}


function mapUsernameProductRow(
  row: Record<string, unknown>,
  creatorUsername?: string | null,
): ShopCatalogProduct | null {
  const postId = row.post_id != null ? String(row.post_id).trim() : "";
  if (!postId) return null;
  const usernameHint =
    creatorUsername ||
    String(row.username ?? row.user_name ?? row.handle ?? "").trim() ||
    null;
  const name = sanitizePublicCardTitle(
    row.product_name ?? row.content_title ?? row.shop_title ?? "",
    {
      ...row,
      type: row.type || "PRODUCT",
      platform: row.platform,
      product_name: row.product_name,
      shop_title: row.shop_title,
      content_title: row.content_title,
      username: usernameHint,
    },
    { creatorUsername: usernameHint },
  );
  const url = String(row.shop_link ?? row.external_link ?? "").trim();
  // Keep thumbnail / media_url first so shop tiles stay unchanged.
  // Search product rows often have the still only on product_image.
  const image =
    contentImageUrl(row) ||
    stillUrl(row.product_image) ||
    stillUrl(row.table_image);
  const breadcrumbsRaw = String(row.breadcrumbs ?? "").trim();
  return {
    postId,
    name: name,
    url: url || "#",
    image,
    contentCategory: String(row.content_category ?? "").trim(),
    platform: String(row.platform ?? "").trim(),
    breadcrumbsRaw,
    breadcrumbs: parseProductBreadcrumbs(breadcrumbsRaw),
  };
}

function applyProductFilters(query: any, opts?: { requireBreadcrumbs?: boolean }) {
  let q = query.eq("type", "PRODUCT").eq("index", "active");
  if (opts?.requireBreadcrumbs) {
    q = q.not("breadcrumbs", "is", null).neq("breadcrumbs", "");
  }
  return q;
}

function shopTrendingDateRange(): { startDate: string; endDate: string } {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    startDate: dayjs().tz(tz).utc().subtract(TRENDING_DAYS, "day").format("YYYY-MM-DD"),
    endDate: dayjs().tz(tz).utc().toISOString(),
  };
}

export type ShopProductFetchResult = {
  products: ShopCatalogProduct[];
  hasBreadcrumbsColumn: boolean;
};

async function queryUsernameProducts(args: {
  username: string;
  userId: string;
  limit?: number;
  selectCols: string;
  contentCategory?: string | null;
  postIds?: string[];
  requireBreadcrumbs?: boolean;
  rangeFrom?: number;
  rangeTo?: number;
}): Promise<{ products: ShopCatalogProduct[]; hasBreadcrumbsColumn: boolean }> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  if (!username || !userId) {
    return { products: [], hasBreadcrumbsColumn: false };
  }

  const limit = args.limit ?? DEFAULT_FETCH_LIMIT;
  let query = applyProductFilters(
    supabase
      .from(username)
      .select(args.selectCols)
      .eq("user_id", userId),
    { requireBreadcrumbs: args.requireBreadcrumbs },
  ).order("post_date", { ascending: false });

  if (args.rangeFrom != null && args.rangeTo != null) {
    query = query.range(args.rangeFrom, args.rangeTo);
  } else {
    query = query.limit(limit);
  }

  const category = args.contentCategory?.trim();
  if (category) {
    query = query.eq("content_category", category);
  }

  if (args.postIds?.length) {
    query = query.in("post_id", args.postIds);
  }

  const { data, error } = await query;
  if (error) {
    if (isMissingBreadcrumbsColumn(error) && args.selectCols.includes("breadcrumbs")) {
      return queryUsernameProducts({
        ...args,
        selectCols: BASE_PRODUCT_COLS,
        requireBreadcrumbs: false,
      });
    }
    console.warn("[templateShopCatalog] fetch products failed:", error.message);
    return { products: [], hasBreadcrumbsColumn: !isMissingBreadcrumbsColumn(error) };
  }

  return {
    products: (data ?? [])
      .map((row: Record<string, unknown>) =>
        mapUsernameProductRow(row, username),
      )
      .filter((p: ShopCatalogProduct | null): p is ShopCatalogProduct => Boolean(p)),
    hasBreadcrumbsColumn: args.selectCols.includes("breadcrumbs"),
  };
}

/**
 * Map of `post_id -> shoppable URL` for a creator's shoppable content.
 *
 * The website-template search feed sources rows from the single-user search RPCs,
 * which return posts WITHOUT `shop_link`, so the shop button never appears on search
 * results even for content that is shoppable elsewhere. This map lets the search
 * results backfill `shop_link` by `post_id` from the same per-username table the Shop
 * page reads from — keeping search consistent with the rest of the site.
 *
 * The URL is resolved as `shop_link ?? external_link` (mirroring `mapUsernameProductRow`):
 * many affiliate products (Amazon/LTK/ShopMy) store their shoppable URL in `external_link`,
 * not `shop_link`, so a `shop_link`-only lookup would miss them.
 */
export async function fetchTemplateShopLinkMap(args: {
  username: string;
  userId: string;
  limit?: number;
}): Promise<Record<string, string>> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const map: Record<string, string> = {};
  if (!username || !userId) return map;

  // Preferred query: pull both `shop_link` and `external_link`. If the table
  // doesn't have an `external_link` column (varies per creator table), this 400s
  // — so fall back to a `shop_link`-only query instead of returning nothing.
  let rows: Array<Record<string, unknown>> = [];
  const primary = await supabase
    .from(username)
    .select("post_id, shop_link, external_link, tagged_link, platform")
    .eq("user_id", userId)
    .or("shop_link.not.is.null,external_link.not.is.null,tagged_link.not.is.null")
    .limit(args.limit ?? 5000);

  if (primary.error) {
    console.warn(
      "[templateShopCatalog] shop_link+tagged_link fetch failed, retrying without tagged_link:",
      primary.error.message,
    );
    const secondary = await supabase
      .from(username)
      .select("post_id, shop_link, external_link, platform")
      .eq("user_id", userId)
      .or("shop_link.not.is.null,external_link.not.is.null")
      .limit(args.limit ?? 5000);
    if (secondary.error) {
      console.warn(
        "[templateShopCatalog] shop_link+external_link fetch failed, retrying shop_link-only:",
        secondary.error.message,
      );
      const fallback = await supabase
        .from(username)
        .select("post_id, shop_link, platform")
        .eq("user_id", userId)
        .not("shop_link", "is", null)
        .limit(args.limit ?? 5000);
      if (fallback.error) {
        console.warn("[templateShopCatalog] shop_link map fetch failed:", fallback.error.message);
        return map;
      }
      rows = fallback.data ?? [];
    } else {
      rows = secondary.data ?? [];
    }
  } else {
    rows = primary.data ?? [];
  }

  for (const row of rows) {
    const r = row as {
      post_id?: unknown;
      shop_link?: unknown;
      external_link?: unknown;
      tagged_link?: unknown;
      platform?: unknown;
    };
    const postId = r.post_id != null ? String(r.post_id).trim() : "";
    // Skip social profile mentions — same filter as resolveShopButtonCta.
    const link = resolvePostShopUrl(r);
    if (postId && link) map[postId] = link;
  }
  return map;
}

function collectionSlug(shopLink: string): string {
  try {
    const parts = new URL(shopLink).pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  } catch {
    return "";
  }
}

async function collectionRecord(
  slug: string,
): Promise<{ postIds: string[]; query: string; userId: string } | null> {
  if (!slug) return null;
  const tables = [
    "stayuptodate_components",
    "template_components",
    "page_components",
  ];
  for (const table of tables) {
    for (const column of ["unique_id", "route_name"] as const) {
      const { data, error } = await supabase
        .from(table)
        .select("post_ids, query, username, user_id")
        .eq(column, slug)
        .limit(8);
      if (error || !Array.isArray(data) || !data.length) continue;
      const row =
        data.find(
          (item) =>
            String((item as { username?: string }).username || "").toLowerCase() ===
            "shop.with.caroline",
        ) || data[0];
      const rec = row as {
        post_ids?: string;
        query?: string;
        user_id?: string;
      };
      const postIds = String(rec.post_ids || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .slice(0, 7);
      const query = String(rec.query || "").trim();
      if (!postIds.length && !query) continue;
      return {
        postIds,
        query,
        userId: String(rec.user_id || "").trim(),
      };
    }
  }
  return null;
}

async function productsFromTable(
  table: string,
  postIds: string[],
): Promise<ShopCatalogProduct[]> {
  if (!table || !postIds.length) return [];
  const { data, error } = await supabase
    .from(table)
    .select(
      "post_id, product_name, content_title, shop_link, external_link, media_url, thumbnail, product_image, platform, type, username",
    )
    .in("post_id", postIds);
  if (error || !Array.isArray(data)) return [];
  const byId = new Map<string, ShopCatalogProduct>();
  for (const row of data) {
    const mapped = mapUsernameProductRow(row as Record<string, unknown>, table);
    if (mapped) byId.set(mapped.postId, mapped);
  }
  return postIds
    .map((id) => byId.get(id))
    .filter(Boolean) as ShopCatalogProduct[];
}

/** Products saved on the collection behind a content post's shop link. */
export async function fetchCollectionProductsForBlog(args: {
  username: string;
  sourcePostId?: string;
  shopLink?: string;
}): Promise<ShopCatalogProduct[]> {
  const username = args.username.trim().toLowerCase();
  let shopLink = String(args.shopLink || "").trim();
  const sourcePostId = String(args.sourcePostId || "").trim();
  if (!shopLink && username && sourcePostId) {
    const { data } = await supabase
      .from(username)
      .select("shop_link")
      .eq("post_id", sourcePostId)
      .limit(1)
      .maybeSingle();
    shopLink = String(
      (data as { shop_link?: string } | null)?.shop_link || "",
    ).trim();
  }
  const slug = collectionSlug(shopLink);
  const collection = await collectionRecord(slug);
  if (!collection) return [];
  const ids = collection.postIds.slice(0, 7);
  if (!ids.length && collection.query && collection.userId) {
    const { data, error } = await supabase.rpc("generalfeedshope", {
      userid: collection.userId,
      start_number: 0,
      end_num: 6,
      filter: ["PRODUCT"],
      query_str: collection.query.toLowerCase(),
      user_name: username,
      socials: ["LTK", "Amazon", "ShopMy", "MISC"],
      idx: 0,
      show_ads: false,
    });
    if (error || !Array.isArray(data)) return [];
    return data
      .slice(0, 7)
      .map((row) =>
        mapUsernameProductRow(row as Record<string, unknown>, username),
      )
      .filter(Boolean) as ShopCatalogProduct[];
  }
  if (!ids.length) return [];
  const tables = [username, "shop.with.caroline"].filter(
    (table, index, all) => table && all.indexOf(table) === index,
  );
  for (const table of tables) {
    const products = await productsFromTable(table, ids);
    if (products.length) return products;
  }
  return [];
}

/** Product rows for a known set of `{username}.post_id` values (collection members). */
export async function fetchTemplateShopProductsByIds(args: {
  username: string;
  userId: string;
  postIds: string[];
  limit?: number;
}): Promise<ShopCatalogProduct[]> {
  const ids = Array.from(
    new Set((args.postIds ?? []).map((id) => String(id || "").trim()).filter(Boolean)),
  );
  if (!args.username?.trim() || !args.userId?.trim() || ids.length === 0) {
    return [];
  }

  // PostgREST `.in()` URLs blow up / drop the connection past a few hundred ids.
  const CHUNK = 80;
  const byId = new Map<string, ShopCatalogProduct>();
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const { products } = await queryUsernameProducts({
      username: args.username,
      userId: args.userId,
      postIds: chunk,
      limit: args.limit ?? chunk.length,
      selectCols: PRODUCT_COLS_WITH_BREADCRUMBS,
    });
    for (const p of products) byId.set(p.postId, p);
  }

  const out: ShopCatalogProduct[] = [];
  for (const id of ids) {
    const p = byId.get(id);
    if (p) out.push(p);
  }
  return out;
}

export type ShopBrowseCatalogPage = {
  products: ShopCatalogProduct[];
  hasBreadcrumbsColumn: boolean;
  rawCount: number;
  pageSize: number;
};

/** One PostgREST page of PRODUCT rows for the browse index. */
export async function fetchTemplateShopBrowseCatalogPage(args: {
  username: string;
  userId: string;
  offset?: number;
}): Promise<ShopBrowseCatalogPage> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const offset = Math.max(0, args.offset ?? 0);
  const empty: ShopBrowseCatalogPage = {
    products: [],
    hasBreadcrumbsColumn: false,
    rawCount: 0,
    pageSize: BROWSE_PAGE_SIZE,
  };
  if (!username || !userId) return empty;

  const pageLimit = Math.min(BROWSE_PAGE_SIZE, BROWSE_INDEX_MAX_ROWS - offset);
  if (pageLimit <= 0) {
    return { ...empty, hasBreadcrumbsColumn: true };
  }

  const page = await queryUsernameProducts({
    username,
    userId,
    limit: pageLimit,
    selectCols: PRODUCT_COLS_WITH_BREADCRUMBS,
    requireBreadcrumbs: true,
    rangeFrom: offset,
    rangeTo: offset + pageLimit - 1,
  });

  if (!page.hasBreadcrumbsColumn) return empty;

  return {
    products: page.products,
    hasBreadcrumbsColumn: true,
    rawCount: page.products.length,
    pageSize: pageLimit,
  };
}

/** Paginated fetch of PRODUCT rows that have a breadcrumbs value. */
export async function fetchTemplateShopBrowseCatalog(args: {
  username: string;
  userId: string;
}): Promise<ShopProductFetchResult> {
  const all: ShopCatalogProduct[] = [];
  let offset = 0;
  let hasBreadcrumbsColumn = true;

  while (offset < BROWSE_INDEX_MAX_ROWS) {
    const page = await fetchTemplateShopBrowseCatalogPage({
      username: args.username,
      userId: args.userId,
      offset,
    });
    hasBreadcrumbsColumn = page.hasBreadcrumbsColumn;
    if (!page.hasBreadcrumbsColumn) {
      return { products: [], hasBreadcrumbsColumn: false };
    }

    all.push(...page.products);
    if (page.rawCount < page.pageSize) break;
    offset += page.pageSize;
  }

  return {
    products: all,
    hasBreadcrumbsColumn,
  };
}

/** All active PRODUCT rows from `{username}` (includes untagged). */
export async function fetchTemplateShopProducts(args: {
  username: string;
  userId: string;
  limit?: number;
  contentCategory?: string | null;
}): Promise<ShopProductFetchResult> {
  return queryUsernameProducts({
    username: args.username,
    userId: args.userId,
    limit: args.limit,
    selectCols: PRODUCT_COLS_WITH_BREADCRUMBS,
    contentCategory: args.contentCategory,
  });
}

export type ShopCategoryTab = {
  label: string;
  slug: string;
  count: number;
};

/** Top `content_category` values (fallback when breadcrumbs column absent). */
export async function fetchTemplateShopCategoryTabs(args: {
  username: string;
  userId: string;
  maxTabs?: number;
}): Promise<ShopCategoryTab[]> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  if (!username || !userId) return [];

  const { data, error } = await supabase
    .from(username)
    .select("content_category")
    .eq("user_id", userId)
    .eq("type", "PRODUCT")
    .eq("index", "active")
    .limit(5000);

  if (error) {
    console.warn("[templateShopCatalog] category tabs failed:", error.message);
    return [];
  }

  // Count exact labels first, then collapse by slug. Distinct labels can
  // slugify to the same key (e.g. "Jewelry" / "jewelry" → "jewelry"), which
  // would otherwise produce duplicate React keys and duplicate tabs.
  const counts = new Map<string, number>();
  for (const row of data ?? []) {
    const cat = String(
      (row as { content_category?: string }).content_category ?? "",
    ).trim();
    if (!cat) continue;
    counts.set(cat, (counts.get(cat) ?? 0) + 1);
  }

  const bySlug = new Map<
    string,
    { label: string; count: number; labelCount: number }
  >();
  for (const [label, count] of counts.entries()) {
    const slug = slugifyShopCategory(label);
    if (!slug) continue;
    const prev = bySlug.get(slug);
    if (!prev) {
      bySlug.set(slug, { label, count, labelCount: count });
      continue;
    }
    bySlug.set(slug, {
      // Prefer the casing/variant with the most products for .eq() fetches.
      label: count > prev.labelCount ? label : prev.label,
      count: prev.count + count,
      labelCount: Math.max(prev.labelCount, count),
    });
  }

  return [...bySlug.entries()]
    .map(([slug, v]) => ({ label: v.label, slug, count: v.count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, args.maxTabs ?? MAX_CATEGORY_TABS);
}

/** Trending products ordered by shop_link clicks.
 *  Prefers template-native analytics (template_analytics table); falls back to
 *  legacy link-in-bio analytics if no template data exists yet. */
export async function fetchTemplateTrendingShopProducts(args: {
  username: string;
  userId: string;
  limit?: number;
}): Promise<ShopProductFetchResult> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  if (!username || !userId) {
    return { products: [], hasBreadcrumbsColumn: false };
  }

  const limit = args.limit ?? 60;
  const { startDate, endDate } = shopTrendingDateRange();

  // Try template-native analytics first (website template shop clicks).
  // Site-wide — same scope as Top shopped / getTemplateShoppablePostIds
  // (Search + Home shop clicks must count, not only Products page_key).
  let orderedIds: string[] = [];
  try {
    const templateClicks = await getTemplateShoppableClicks(
      username,
      limit,
      startDate,
      endDate,
      userId,
      null,
    );
    orderedIds = (templateClicks ?? [])
      .map((c) => String(c.post_id ?? "").trim())
      .filter(Boolean);
  } catch {
    // ignore — fall through to legacy
  }

  // Fall back to legacy link-in-bio analytics when no template data yet.
  if (orderedIds.length === 0) {
    try {
      const legacyClicks = await getShoppableClicks(username, limit, startDate, endDate, userId);
      orderedIds = (legacyClicks ?? [])
        .map((c) => String(c.post_id ?? "").trim())
        .filter(Boolean);
    } catch (e) {
      console.warn("[templateShopCatalog] trending clicks failed:", e);
      return { products: [], hasBreadcrumbsColumn: false };
    }
  }
  if (orderedIds.length === 0) {
    return { products: [], hasBreadcrumbsColumn: false };
  }

  const { products, hasBreadcrumbsColumn } = await queryUsernameProducts({
    username,
    userId,
    selectCols: PRODUCT_COLS_WITH_BREADCRUMBS,
    postIds: orderedIds,
    limit: orderedIds.length,
  });

  const byId = new Map(products.map((p) => [p.postId, p]));
  const out: ShopCatalogProduct[] = [];
  for (const id of orderedIds) {
    const p = byId.get(id);
    if (p) out.push(p);
  }
  return { products: out, hasBreadcrumbsColumn };
}

export function findShopCategoryLabelBySlug(
  tabs: ShopCategoryTab[],
  slug: string,
): string | null {
  const want = slug.trim().toLowerCase();
  if (!want) return null;
  const hit = tabs.find((t) => t.slug === want);
  return hit?.label ?? null;
}

// -----------------------------------------------------------------------------
// RPC-backed paginated catalog (see sql/template_shop_catalog_rpcs.sql)
// -----------------------------------------------------------------------------

export type ShopFacetNavItem = {
  label: string;
  slug: string;
  count: number;
  sample_image?: string | null;
};

export type ShopProductsFacets = {
  categories: ShopFacetNavItem[];
  primaries: ShopFacetNavItem[];
  secondariesByPrimarySlug: Record<string, ShopFacetNavItem[]>;
  subsByPathSlug: Record<string, ShopFacetNavItem[]>;
  brandsByPathSlug: Record<string, ShopFacetNavItem[]>;
};

export type TemplateShopProductsRpcPage = {
  products: ShopCatalogProduct[];
  total: number;
  pageSize: number;
  offset: number;
  hasBreadcrumbsColumn: boolean;
  facets: ShopProductsFacets | null;
  /** True when PostgREST fallback was used (RPC missing). */
  usedFallback: boolean;
};

export type TemplateShopContentRpcPage = {
  items: ShopCatalogContent[];
  total: number;
  pageSize: number;
  offset: number;
  platforms: ShopContentNavItem[];
  formatsByPlatform: Record<string, ShopContentNavItem[]>;
  usedFallback: boolean;
};

export type TemplateCreatorLooksRpcPage = {
  items: ShopCatalogContent[];
  total: number;
  pageSize: number;
  offset: number;
  usedFallback: boolean;
};

function isMissingRpcError(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  const msg = String(error.message ?? "").toLowerCase();
  const code = String(error.code ?? "");
  return (
    code === "PGRST202" ||
    code === "42883" ||
    msg.includes("could not find the function") ||
    msg.includes("function public.get_template_shop_") ||
    msg.includes("function public.get_template_creator_looks_page") ||
    (msg.includes("does not exist") && msg.includes("function"))
  );
}

let loggedMissingShopRpc = false;
function warnMissingShopRpcOnce(name: string, error: { message?: string } | null) {
  if (loggedMissingShopRpc) return;
  loggedMissingShopRpc = true;
  console.warn(
    `[templateShopCatalog] ${name} RPC missing — using PostgREST fallback. Apply sql/template_shop_catalog_rpcs.sql.`,
    error?.message,
  );
}

function mapRpcNavItems(raw: unknown): ShopFacetNavItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      const r = row as Record<string, unknown>;
      const slug = String(r.slug ?? "").trim();
      const label = String(r.label ?? "").trim();
      const count = Number(r.count ?? 0);
      if (!slug || !label) return null;
      const sample = r.sample_image != null ? String(r.sample_image).trim() : "";
      return {
        label,
        slug,
        count: Number.isFinite(count) ? count : 0,
        sample_image: sample || null,
      };
    })
    .filter((x): x is ShopFacetNavItem => Boolean(x));
}

function mapRpcNavMap(raw: unknown): Record<string, ShopFacetNavItem[]> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, ShopFacetNavItem[]> = {};
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    out[key] = mapRpcNavItems(val);
  }
  return out;
}

function mapRpcContentNavItems(raw: unknown): ShopContentNavItem[] {
  return mapRpcNavItems(raw).map(({ slug, count }) => ({
    slug,
    count,
    label: shopContentPlatformLabel(slug),
  }));
}

function mapRpcContentNavMap(raw: unknown): Record<string, ShopContentNavItem[]> {
  const mapped = mapRpcNavMap(raw);
  const out: Record<string, ShopContentNavItem[]> = {};
  for (const [k, v] of Object.entries(mapped)) {
    out[k] = v.map(({ label, slug, count }) => ({ label, slug, count }));
  }
  return out;
}

function facetsFromRpc(raw: unknown): ShopProductsFacets | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    categories: mapRpcNavItems(r.categories),
    primaries: mapRpcNavItems(r.primaries),
    secondariesByPrimarySlug: mapRpcNavMap(r.secondariesByPrimarySlug),
    subsByPathSlug: mapRpcNavMap(r.subsByPathSlug),
    brandsByPathSlug: mapRpcNavMap(r.brandsByPathSlug),
  };
}

/** Build ShopBrowseIndex from RPC facets (no full product dump). */
export function browseIndexFromProductsFacets(
  facets: ShopProductsFacets | null | undefined,
): import("./shopBreadcrumbs").ShopBrowseIndex {
  if (!facets) {
    return {
      primaries: [],
      secondariesByPrimarySlug: {},
      subsByPathSlug: {},
      brandsByPathSlug: {},
    };
  }
  const toNav = (items: ShopFacetNavItem[]) =>
    items.map(({ label, slug, count }) => ({ label, slug, count }));
  return {
    primaries: toNav(facets.primaries),
    secondariesByPrimarySlug: Object.fromEntries(
      Object.entries(facets.secondariesByPrimarySlug).map(([k, v]) => [k, toNav(v)]),
    ),
    subsByPathSlug: Object.fromEntries(
      Object.entries(facets.subsByPathSlug).map(([k, v]) => [k, toNav(v)]),
    ),
    brandsByPathSlug: Object.fromEntries(
      Object.entries(facets.brandsByPathSlug).map(([k, v]) => [k, toNav(v)]),
    ),
  };
}

export function categoryTabsFromProductsFacets(
  facets: ShopProductsFacets | null | undefined,
): ShopCategoryTab[] {
  if (!facets?.categories?.length) return [];
  return facets.categories.map(({ label, slug, count }) => ({ label, slug, count }));
}

async function fallbackProductsPage(args: {
  username: string;
  userId: string;
  offset: number;
  limit: number;
  contentCategory?: string | null;
}): Promise<TemplateShopProductsRpcPage> {
  const page = await queryUsernameProducts({
    username: args.username,
    userId: args.userId,
    contentCategory: args.contentCategory,
    selectCols: PRODUCT_COLS_WITH_BREADCRUMBS,
    rangeFrom: args.offset,
    rangeTo: args.offset + args.limit - 1,
    limit: args.limit,
  });
  return {
    products: page.products,
    total: page.products.length < args.limit ? args.offset + page.products.length : args.offset + args.limit + 1,
    pageSize: args.limit,
    offset: args.offset,
    hasBreadcrumbsColumn: page.hasBreadcrumbsColumn,
    facets: null,
    usedFallback: true,
  };
}

function contentTypesForFormatSlug(
  formatSlug?: string | null,
  platformSlug?: string | null,
): string[] | null {
  const f = String(formatSlug || "")
    .trim()
    .toLowerCase();
  const p = String(platformSlug || "")
    .trim()
    .toLowerCase();
  if (!f) return null;
  if (p === "instagram") {
    if (f === "reels") return ["REELS"];
    if (f === "stories") return ["STORY", "HIGHLIGHTS"];
    if (f === "posts") return ["FEED", "POST"];
  }
  if (p === "youtube") {
    if (f === "shorts") return ["SHORTS", "SHORT", "REELS"];
  }
  return null;
}

function platformIlikeValues(platformSlug?: string | null): string[] {
  const p = String(platformSlug || "")
    .trim()
    .toLowerCase();
  if (!p) return [];
  if (p === "twitter") return ["twitter", "x"];
  if (p === "others" || p === "other") return ["misc-content", "misc_content", "others", "other"];
  return [p];
}

/**
 * Content grid for `shoppable=yes`: only rows with a real `shop_link`.
 * Does not treat `external_link` / `tagged_link` as shoppable (matches Shop pill).
 */
async function fetchShopLinkOnlyContentPage(args: {
  username: string;
  userId: string;
  offset: number;
  limit: number;
  platformSlug?: string | null;
  formatSlug?: string | null;
  query?: string | null;
}): Promise<TemplateShopContentRpcPage> {
  const empty: TemplateShopContentRpcPage = {
    items: [],
    total: 0,
    pageSize: args.limit,
    offset: args.offset,
    platforms: [],
    formatsByPlatform: {},
    usedFallback: true,
  };

  const key = contentShapeKey(args.username, args.userId);
  const cached = contentShapeByTable.get(key);
  const candidates = cached ? [cached.selectCols] : CONTENT_COL_CANDIDATES;
  const platformVals = platformIlikeValues(args.platformSlug);
  const types = contentTypesForFormatSlug(args.formatSlug, args.platformSlug);
  const needle = String(args.query ?? "").trim();

  for (const selectCols of candidates) {
    let q = supabase
      .from(args.username)
      .select(selectCols, { count: "exact" })
      .eq("user_id", args.userId)
      .eq("index", "active")
      .neq("type", "PRODUCT")
      .not("shop_link", "is", null)
      .neq("shop_link", "")
      .order("post_date", { ascending: false });

    if (platformVals.length === 1) {
      q = q.ilike("platform", platformVals[0]!);
    } else if (platformVals.length > 1) {
      q = q.or(
        platformVals.map((v) => `platform.ilike."${v}"`).join(","),
      );
    }

    if (types?.length) {
      q = q.in("type", types);
    }

    if (needle) {
      const safe = needle.replace(/[%_,]/g, "");
      if (safe) {
        q = q.or(
          `caption.ilike.%${safe}%,content_title.ilike.%${safe}%`,
        );
      }
    }

    const page = await q.range(args.offset, args.offset + args.limit - 1);
    if (page.error) {
      console.warn(
        "[templateShopCatalog] shop_link content page failed, retrying:",
        page.error.message,
      );
      continue;
    }
    if (!cached) contentShapeByTable.set(key, { selectCols });

    const mapped = mapContentRows(
      (page.data as Record<string, unknown>[] | null) ?? [],
    ).filter((item) =>
      contentMatchesShopFilter(
        item,
        args.platformSlug,
        args.formatSlug,
        "yes",
      ),
    );

    const total =
      typeof page.count === "number" && page.count >= 0
        ? page.count
        : args.offset + mapped.length;

    return {
      items: mapped,
      total,
      pageSize: args.limit,
      offset: args.offset,
      platforms: [],
      formatsByPlatform: {},
      usedFallback: true,
    };
  }

  return empty;
}

async function fallbackContentPage(args: {
  username: string;
  userId: string;
  offset: number;
  limit: number;
  platformSlug?: string | null;
  formatSlug?: string | null;
  shoppable?: "all" | "yes" | "no" | null;
  query?: string | null;
}): Promise<TemplateShopContentRpcPage> {
  if (args.shoppable === "yes") {
    return fetchShopLinkOnlyContentPage(args);
  }

  const legacy = await fetchTemplateShopContentPage({
    username: args.username,
    userId: args.userId,
    offset: args.offset,
    limit: args.limit,
  });
  const items = legacy.items
    .filter((item) =>
      contentMatchesShopFilter(
        item,
        args.platformSlug,
        args.formatSlug,
        args.shoppable,
      ),
    )
    .slice(0, args.limit);
  return {
    items,
    total:
      legacy.rawCount < legacy.pageSize
        ? args.offset + items.length
        : args.offset + args.limit + 1,
    pageSize: args.limit,
    offset: args.offset,
    platforms: [],
    formatsByPlatform: {},
    usedFallback: true,
  };
}

export async function fetchTemplateShopProductsRpcPage(args: {
  username: string;
  userId: string;
  offset?: number;
  limit?: number;
  contentCategory?: string | null;
  primarySlug?: string | null;
  secondarySlug?: string | null;
  subSlug?: string | null;
  brandSlug?: string | null;
  brandSlugs?: string[] | null;
  requireBreadcrumbs?: boolean;
  query?: string | null;
  includeFacets?: boolean;
}): Promise<TemplateShopProductsRpcPage> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const offset = Math.max(0, args.offset ?? 0);
  const limit = Math.max(1, Math.min(args.limit ?? TEMPLATE_SHOP_PAGE_SIZE, 100));
  const empty: TemplateShopProductsRpcPage = {
    products: [],
    total: 0,
    pageSize: limit,
    offset,
    hasBreadcrumbsColumn: false,
    facets: null,
    usedFallback: false,
  };
  if (!username || !userId) return empty;

  const includeFacets = args.includeFacets ?? offset === 0;
  const { data, error } = await supabase.rpc("get_template_shop_products_page", {
    p_username: username,
    p_user_id: userId,
    p_limit: limit,
    p_offset: offset,
    p_content_category: args.contentCategory?.trim() || null,
    p_primary_slug: args.primarySlug?.trim() || null,
    p_secondary_slug: args.secondarySlug?.trim() || null,
    p_sub_slug: args.subSlug?.trim() || null,
    p_brand_slug: args.brandSlug?.trim() || null,
    p_brand_slugs: args.brandSlugs?.length ? args.brandSlugs : null,
    p_require_breadcrumbs: Boolean(args.requireBreadcrumbs),
    p_query: args.query?.trim() || null,
    p_include_facets: includeFacets,
  });

  if (error) {
    if (isMissingRpcError(error)) {
      warnMissingShopRpcOnce("get_template_shop_products_page", error);
      return fallbackProductsPage({
        username,
        userId,
        offset,
        limit,
        contentCategory: args.contentCategory,
      });
    }
    console.warn("[templateShopCatalog] products RPC failed:", error.message);
    return empty;
  }

  const payload = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(payload.items) ? payload.items : [];
  let products = rows
    .map((row) => {
      const r = row as Record<string, unknown>;
      // RPC already filters type=PRODUCT; force type so mapper accepts all platforms.
      return mapFeedPostToShopCatalogProduct(
        { ...r, type: r.type || "PRODUCT" },
        undefined,
        username,
      );
    })
    .filter((p): p is ShopCatalogProduct => Boolean(p));

  let total = Number(payload.total ?? products.length) || 0;

  // Bags & Purses root: affiliate crumbs use `Women's | Bags | …` instead of
  // primary `Bags & Purses`. Merge those on the first page so clutches/totes
  // aren't missing from the category browse (search already finds them by name).
  const primarySlug = args.primarySlug?.trim().toLowerCase() || "";
  const wantBagsAlias =
    primarySlug === "bags-purses" &&
    !args.secondarySlug?.trim() &&
    !args.subSlug?.trim() &&
    !args.brandSlug?.trim() &&
    !(args.brandSlugs || []).length &&
    !args.query?.trim() &&
    offset === 0;
  if (wantBagsAlias) {
    try {
      const { data: aliasRows } = await supabase
        .from(username)
        .select(
          "post_id, product_name, shop_title, content_title, shop_link, media_url, thumbnail, product_image, platform, type, breadcrumbs, post_date, index",
        )
        .eq("user_id", userId)
        .eq("type", "PRODUCT")
        .ilike("index", "%active%")
        .ilike("breadcrumbs", "Women's | Bags%")
        .order("post_date", { ascending: false })
        .limit(100);
      const seen = new Set(products.map((p) => p.postId));
      const aliases: ShopCatalogProduct[] = [];
      for (const row of Array.isArray(aliasRows) ? aliasRows : []) {
        const mapped = mapFeedPostToShopCatalogProduct(
          { ...(row as Record<string, unknown>), type: "PRODUCT" },
          undefined,
          username,
        );
        if (!mapped || seen.has(mapped.postId)) continue;
        if (!productMatchesBagsPursesPrimaryAlias(mapped.breadcrumbs)) continue;
        seen.add(mapped.postId);
        aliases.push(mapped);
      }
      if (aliases.length) {
        products = [...aliases, ...products];
        total += aliases.length;
      }
    } catch (e) {
      console.warn(
        "[templateShopCatalog] bags-purses alias merge failed:",
        e,
      );
    }
  }

  return {
    products,
    total,
    pageSize: Number(payload.pageSize ?? limit) || limit,
    offset: Number(payload.offset ?? offset) || offset,
    hasBreadcrumbsColumn: Boolean(payload.has_breadcrumbs_column),
    facets: includeFacets ? facetsFromRpc(payload.facets) : null,
    usedFallback: false,
  };
}

export async function fetchTemplateShopContentRpcPage(args: {
  username: string;
  userId: string;
  offset?: number;
  limit?: number;
  platformSlug?: string | null;
  formatSlug?: string | null;
  shoppable?: "all" | "yes" | "no" | null;
  query?: string | null;
  includeFacets?: boolean;
}): Promise<TemplateShopContentRpcPage> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const offset = Math.max(0, args.offset ?? 0);
  const limit = Math.max(1, Math.min(args.limit ?? TEMPLATE_SHOP_PAGE_SIZE, 100));
  const empty: TemplateShopContentRpcPage = {
    items: [],
    total: 0,
    pageSize: limit,
    offset,
    platforms: [],
    formatsByPlatform: {},
    usedFallback: false,
  };
  if (!username || !userId) return empty;

  const includeFacets = args.includeFacets ?? offset === 0;
  const shoppable = args.shoppable || "all";

  // RPC `p_shoppable=yes` historically treated external_link / tagged_link as
  // shoppable. Content "Shoppable" must match the Shop pill: true shop_link only.
  if (shoppable === "yes") {
    return fetchShopLinkOnlyContentPage({
      username,
      userId,
      offset,
      limit,
      platformSlug: args.platformSlug,
      formatSlug: args.formatSlug,
      query: args.query,
    });
  }

  let data: unknown = null;
  let error: { message?: string; code?: string; details?: string; hint?: string } | null =
    null;
  try {
    const res = await supabase.rpc("get_template_shop_content_page", {
      p_username: username,
      p_user_id: userId,
      p_limit: limit,
      p_offset: offset,
      p_platform_slug: args.platformSlug?.trim() || null,
      p_format_slug: args.formatSlug?.trim() || null,
      p_shoppable: shoppable,
      p_query: args.query?.trim() || null,
      p_include_facets: includeFacets,
    });
    data = res.data;
    error = res.error;
  } catch (e) {
    console.warn("[templateShopCatalog] content RPC threw:", e);
    const fb = await fallbackContentPage({
      username,
      userId,
      offset,
      limit,
      platformSlug: args.platformSlug,
      formatSlug: args.formatSlug,
      shoppable,
      query: args.query,
    });
    if (includeFacets && fb.items.length) {
      const idx = buildShopContentPlatformIndex(fb.items);
      return {
        ...fb,
        platforms: idx.platforms,
        formatsByPlatform: idx.formatsByPlatform,
      };
    }
    return fb;
  }

  if (error) {
    if (
      isMissingRpcError(error) ||
      /failed to fetch|network|quic|http2|connection/i.test(String(error.message || ""))
    ) {
      if (isMissingRpcError(error)) {
        warnMissingShopRpcOnce("get_template_shop_content_page", error);
      } else {
        console.warn(
          "[templateShopCatalog] content RPC failed, using PostgREST fallback:",
          error.message,
        );
      }
      const fb = await fallbackContentPage({
        username,
        userId,
        offset,
        limit,
        platformSlug: args.platformSlug,
        formatSlug: args.formatSlug,
        shoppable,
        query: args.query,
      });
      if (includeFacets && fb.items.length) {
        const idx = buildShopContentPlatformIndex(fb.items);
        return {
          ...fb,
          platforms: idx.platforms,
          formatsByPlatform: idx.formatsByPlatform,
        };
      }
      return fb;
    }
    console.warn("[templateShopCatalog] content RPC failed:", error.message);
    return empty;
  }

  const payload = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(payload.items) ? payload.items : [];
  const items = rows
    .map((row) => mapFeedPostToShopCatalogContent(row as Record<string, unknown>))
    .filter((c): c is ShopCatalogContent => Boolean(c))
    .filter((c) =>
      contentMatchesShopFilter(
        c,
        args.platformSlug,
        args.formatSlug,
        shoppable,
      ),
    );

  return {
    items,
    total: Number(payload.total ?? items.length) || 0,
    pageSize: Number(payload.pageSize ?? limit) || limit,
    offset: Number(payload.offset ?? offset) || offset,
    platforms: mapRpcContentNavItems(payload.platforms),
    formatsByPlatform: mapRpcContentNavMap(payload.formats_by_platform),
    usedFallback: false,
  };
}

export async function fetchTemplateCreatorLooksRpcPage(args: {
  username: string;
  userId: string;
  offset?: number;
  limit?: number;
}): Promise<TemplateCreatorLooksRpcPage> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const offset = Math.max(0, args.offset ?? 0);
  const limit = Math.max(1, Math.min(args.limit ?? TEMPLATE_SHOP_PAGE_SIZE, 100));
  const empty: TemplateCreatorLooksRpcPage = {
    items: [],
    total: 0,
    pageSize: limit,
    offset,
    usedFallback: false,
  };
  if (!username || !userId) return empty;

  let data: unknown = null;
  let error: { message?: string; code?: string; details?: string; hint?: string } | null =
    null;
  try {
    const res = await supabase.rpc("get_template_creator_looks_page", {
      p_username: username,
      p_user_id: userId,
      p_limit: limit,
      p_offset: offset,
    });
    data = res.data;
    error = res.error;
  } catch (e) {
    console.warn("[templateShopCatalog] looks RPC threw:", e);
    error = { message: e instanceof Error ? e.message : String(e) };
  }

  if (error) {
    if (isMissingRpcError(error) || /failed to fetch|network|quic|http2/i.test(String(error.message || ""))) {
      if (isMissingRpcError(error)) {
        warnMissingShopRpcOnce("get_template_creator_looks_page", error);
      } else {
        console.warn("[templateShopCatalog] looks RPC failed, using content fallback:", error.message);
      }
      // Fallback: scan a small content window client-side for IG looks.
      const legacy = await fetchTemplateShopContentPage({
        username,
        userId,
        offset: 0,
        limit: Math.min(Math.max(offset + limit, TEMPLATE_SHOP_PAGE_SIZE), 100),
      });
      const looks = legacy.items.filter((c) => {
        if (!c.shopUrl) return false;
        const platform = String(c.platformSlug || c.platform || "").toLowerCase();
        if (platform !== "instagram") return false;
        const type = String(c.type || "").toUpperCase();
        return type === "FEED" || type === "REELS" || type === "POST";
      });
      const slice = looks.slice(offset, offset + limit);
      return {
        items: slice,
        total: looks.length,
        pageSize: limit,
        offset,
        usedFallback: true,
      };
    }
    console.warn("[templateShopCatalog] looks RPC failed:", error.message);
    return empty;
  }

  const payload = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(payload.items) ? payload.items : [];
  const items = rows
    .map((row) => mapFeedPostToShopCatalogContent(row as Record<string, unknown>))
    .filter((c): c is ShopCatalogContent => Boolean(c));

  return {
    items,
    total: Number(payload.total ?? items.length) || 0,
    pageSize: Number(payload.pageSize ?? limit) || limit,
    offset: Number(payload.offset ?? offset) || offset,
    usedFallback: false,
  };
}

/**
 * Direct `{username}` lookup for configured Recent Looks / Featured Outfit
 * `postIds` that never appear in the paginated looks RPC window (max 75).
 */
export async function fetchTemplateCreatorLooksByIds(args: {
  username: string;
  userId: string;
  postIds: string[];
}): Promise<ShopCatalogContent[]> {
  const username = args.username.trim().toLowerCase();
  const userId = args.userId.trim();
  const ids = Array.from(
    new Set(
      (args.postIds ?? []).map((id) => String(id || "").trim()).filter(Boolean),
    ),
  ).slice(0, 16);
  if (!username || !userId || ids.length === 0) return [];

  const key = contentShapeKey(username, userId);
  const cached = contentShapeByTable.get(key);
  const candidates = cached ? [cached.selectCols] : CONTENT_COL_CANDIDATES;

  for (const selectCols of candidates) {
    const { data, error } = await supabase
      .from(username)
      .select(selectCols)
      .eq("user_id", userId)
      .eq("index", "active")
      .neq("type", "PRODUCT")
      .in("post_id", ids);
    if (error) {
      console.warn(
        "[templateShopCatalog] looks-by-id failed, retrying:",
        error.message,
      );
      continue;
    }
    if (!cached) contentShapeByTable.set(key, { selectCols });
    const byId = new Map<string, ShopCatalogContent>();
    for (const row of (data as Record<string, unknown>[] | null) ?? []) {
      const item = mapFeedPostToShopCatalogContent(row);
      if (!item?.postId) continue;
      byId.set(item.postId, item);
    }
    const out: ShopCatalogContent[] = [];
    for (const id of ids) {
      const item = byId.get(id);
      if (item) out.push(item);
    }
    return out;
  }

  console.warn("[templateShopCatalog] looks-by-id fetch failed");
  return [];
}
